import { useId, useRef } from 'react'
import { useGridCellEditor, type CustomCellEditorProps } from 'ag-grid-react'
import { Input } from '@/components/ui/input'
import { dropInvalidPercent, useSpreadsheetStart } from './gridEditing'
import {
  autoFill,
  describeAutoFill,
  editablePercent,
  validateWavePercent,
} from './model'
import type { SkuWaveColumn, SkuWaveRow } from './sku-model'
import type { SkuSelectionContext } from './SkuWaveGrid'

export function SkuPercentEditor(
  props: CustomCellEditorProps<SkuWaveRow, number | string> & {
    waveLabel: string
    weekId: string
    weeks: SkuWaveColumn[]
  }
) {
  const input = useRef<HTMLInputElement>(null)
  const id = useId()
  const value =
    typeof props.value === 'number'
      ? editablePercent(props.value)
      : String(props.value ?? '')
  const error = validateWavePercent(value)
  // What else the typed value decides, and who it applies to.
  const fill = error
    ? null
    : autoFill(
        props.weeks.map(week => ({
          weekId: week.id,
          percent: (props.data.waves[week.id]?.percent ?? 0) * 100,
          quantityOverride: !!props.data.waves[week.id]?.quantityOverride,
        })),
        props.weekId,
        value.trim() ? Number(value) : 0
      )
  const fillHint = describeAutoFill(
    fill,
    Object.fromEntries(props.weeks.map(week => [week.id, week.event]))
  )
  const selection = props.context as SkuSelectionContext | undefined
  const selectedCount =
    selection &&
    selection.selected.size > 1 &&
    selection.selected.has(props.data.id)
      ? selection.selected.size
      : 0
  useGridCellEditor({
    isCancelAfterEnd: () => dropInvalidPercent(input.current?.value ?? value),
  })

  useSpreadsheetStart(props.eventKey, props.onValueChange, input)
  const describedBy = [
    error && `${id}-error`,
    fillHint && `${id}-autofill-hint`,
    selectedCount > 0 && `${id}-bulk-edit-hint`,
  ]
    .filter((description): description is string => Boolean(description))
    .join(' ')

  return (
    <div
      className="sku-percent-editor relative h-full w-full"
      onKeyDownCapture={event => {
        if (event.key !== 'Escape') return
        // Cancel once, before AG Grid's cell handler can also schedule a stop.
        event.preventDefault()
        event.stopPropagation()
        props.api.stopEditing(true)
        if (props.node.rowIndex !== null)
          props.api.setFocusedCell(props.node.rowIndex, props.column)
      }}
    >
      <div className="relative h-full">
        <Input
          id={id}
          ref={input}
          aria-label={`${props.data.line?.sku} · ${props.waveLabel} Wave %`}
          value={value}
          inputMode="decimal"
          aria-invalid={!!error}
          aria-describedby={describedBy || undefined}
          title={error ?? fillHint ?? undefined}
          className="h-full w-full rounded-none border-0 pr-6 text-right font-semibold tabular-nums shadow-none focus-visible:ring-2 focus-visible:ring-inset aria-invalid:ring-2 aria-invalid:ring-inset aria-invalid:ring-destructive"
          onChange={event => props.onValueChange(event.target.value)}
        />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          %
        </span>
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="sr-only">
          {error}
        </p>
      )}
      {fillHint && (
        <p
          id={`${id}-autofill-hint`}
          className="sr-only"
          data-testid="sku-autofill-hint"
        >
          {fillHint}
        </p>
      )}
      {selectedCount > 0 && (
        <p
          id={`${id}-bulk-edit-hint`}
          className="sr-only"
          data-testid="sku-bulk-edit-hint"
        >
          Applies to the {selectedCount} selected lines
        </p>
      )}
    </div>
  )
}
